/* ============================================================
   CO-OP RIG – Frontend UI Module (v3 compact)
   ============================================================ */

(function () {
  'use strict';

  /* ── Live State ── */
  const appState = {
    authenticated: false,
    accepted: localStorage.getItem('coop_accepted') === 'true',
    wallet: null,
    expiresAt: null,
    selectedCommunity: localStorage.getItem('selectedCommunity') || 'aquavaults',
    rig: {},       // populated from /api/coop/status
    usage: null,   // populated from /api/coop/me
    rewards: null,         // populated from /api/coop/me
    hashboostBucket: null, // populated from /api/coop/me
    selectedDroplets: 1,
    sessionEndTime: null,   // ms timestamp — set from sessionStartedAt + duration
    cooldownEndTime: null,  // ms timestamp — set from cooldownUntil
    leaderboard: null,      // cached from /api/coop/leaderboard
  };

  let rigPollTimer = null;
  let timerTick    = null;
  const RIG_POLL_INTERVAL = 15000; // 15 seconds

  /* ── API Helpers ── */

  async function api(method, path, body) {
    const opts = {
      method,
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
    };
    if (body) opts.body = JSON.stringify(body);
    const res = await fetch(path, opts);
    const data = await res.json();
    if (!res.ok) throw { status: res.status, ...data };
    return data;
  }

  async function fetchWhoami() {
    return api('GET', '/api/coop/auth/whoami');
  }

  async function fetchChallenge(wallet) {
    return api('POST', '/api/coop/auth/challenge', { wallet });
  }

  async function fetchVerify(wallet, nonce, signature) {
    return api('POST', '/api/coop/auth/verify', { wallet, nonce, signature });
  }

  async function fetchLogout() {
    return api('POST', '/api/coop/auth/logout');
  }

  async function fetchRigStatus() {
    return api('GET', '/api/coop/status');
  }

  async function fetchMe() {
    return api('GET', '/api/coop/me');
  }

  async function submitCountSwaps(community, tokensUsed, swapsAttempted, rigWallet, requestId) {
    return api('POST', '/api/coop/count-swaps', { community, tokensUsed, swapsAttempted, rigWallet, requestId });
  }

  async function submitBurnTokens(community, burnTokens, requestId) {
    return api('POST', '/api/coop/burn-tokens', { community, burnTokens, requestId });
  }

  async function fetchHistory() {
    return api('GET', '/api/coop/history');
  }

  async function fetchLeaderboard() {
    return api('GET', '/api/leaderboard');
  }

  async function fetchSessionHistory() {
    return api('GET', '/api/coop/session-history');
  }

  /* ── DOM Ready ── */
  document.addEventListener('DOMContentLoaded', init);

  async function init() {
    bindTabs();
    bindHowItWorks();
    bindActionTypeToggle();
    bindCommunitySelector();
    bindAcceptanceModal();
    bindActionButtons();
    bindLeaderboardTabs();

    // Check existing session
    try {
      const who = await fetchWhoami();
      if (who.authenticated) {
        appState.authenticated = true;
        appState.wallet = who.wallet;
        appState.expiresAt = who.expiresAt;
      }
    } catch (e) {
      console.warn('[Co-Op Rig] whoami failed:', e);
    }

    renderAuthState();
    renderRigStatus();

    // Leaderboard is public — load on every page visit, no auth needed
    try {
      const lb = await fetchLeaderboard();
      appState.leaderboard = lb;
      renderLeaderboard(lb);
    } catch (e) {
      console.warn('[Co-Op Rig] leaderboard failed:', e);
    }

    // Session history is public — load on every page visit
    try {
      const sh = await fetchSessionHistory();
      renderSessionHistory(sh.sessions || []);
    } catch (e) {
      console.warn('[Co-Op Rig] session-history failed:', e);
    }

    if (appState.authenticated) {
      await refreshAuthenticatedData();
    }

    if (!appState.accepted) {
      showAcceptanceModal();
    }
  }

  async function refreshAuthenticatedData() {
    try {
      const me = await fetchMe();
      if (me.authenticated) {
        appState.usage = me.usageToday;
        appState.rewards = me.rewards;
        appState.hashboostBucket = me.hashboostBucket;
        renderUsage();
        renderRewards();
        renderHashboostBucket();
        await refreshHistory();
      }
    } catch (e) {
      console.warn('[Co-Op Rig] /me failed:', e);
    }

    await refreshRigStatus();
    startRigPolling();
  }

  async function refreshHistory() {
    try {
      var hist = await fetchHistory();
      renderHistory(hist.entries || []);
    } catch (e) {
      console.warn('[Co-Op Rig] history failed:', e);
    }
  }

  function renderHistory(entries) {
    var container = document.getElementById('historyList');
    if (!container) return;

    if (!entries.length) {
      container.innerHTML = '<p style="color:var(--text-secondary);font-size:0.85rem;padding:8px 0;">No contributions yet.</p>';
      return;
    }

    var ACTION_LABEL = { 'count-swaps': 'SWAP', 'burn-tokens': 'BURN', 'hashboost': 'BOOST' };
    var ACTION_CLASS = { 'count-swaps': 'swap', 'burn-tokens': 'burn', 'hashboost': 'hashboost' };

    function timeAgo(isoStr) {
      var diff = Math.floor((Date.now() - new Date(isoStr).getTime()) / 1000);
      if (diff < 60)    return diff + 's ago';
      if (diff < 3600)  return Math.floor(diff / 60) + ' min ago';
      if (diff < 86400) return Math.floor(diff / 3600) + ' hr ago';
      return Math.floor(diff / 86400) + 'd ago';
    }

    function amountText(action, amount) {
      if (action === 'hashboost')   return amount + ' Droplet' + (amount !== 1 ? 's' : '');
      if (action === 'burn-tokens') return amount + ' token' + (amount !== 1 ? 's' : '') + ' (+' + (amount * 2) + ' min)';
      return amount + ' token' + (amount !== 1 ? 's' : '');
    }

    container.innerHTML = entries.map(function (e) {
      var label  = ACTION_LABEL[e.action] || e.action.toUpperCase();
      var cls    = ACTION_CLASS[e.action] || 'swap';
      var weight = Math.round(e.rewardWeight * 1000) / 1000;
      return '<div class="history-item">' +
        '<div class="history-item-type ' + cls + '">' + label + '</div>' +
        '<div class="history-item-details">' +
          '<span class="history-item-community">' + e.community + '</span>' +
          '<span class="history-item-amount">' + amountText(e.action, e.amount) + '</span>' +
        '</div>' +
        '<div class="history-item-meta">' +
          '<span class="history-item-weight">Wt: ' + weight + '</span>' +
          '<span class="history-item-time">' + timeAgo(e.createdAt) + '</span>' +
        '</div>' +
        '<span class="history-item-status confirmed">OK</span>' +
      '</div>';
    }).join('');
  }

  async function refreshRigStatus() {
    try {
      const rig = await fetchRigStatus();
      appState.rig = rig;
      renderRigStatus();
      renderHashboostBucket();
    } catch (e) {
      console.warn('[Co-Op Rig] status failed:', e);
    }
  }

  function startRigPolling() {
    stopRigPolling();
    rigPollTimer = setInterval(refreshRigStatus, RIG_POLL_INTERVAL);
    startTimerTick();
  }

  function stopRigPolling() {
    if (rigPollTimer) {
      clearInterval(rigPollTimer);
      rigPollTimer = null;
    }
    stopTimerTick();
  }

  function startTimerTick() {
    stopTimerTick();
    timerTick = setInterval(tickTimer, 1000);
  }

  function stopTimerTick() {
    if (timerTick) { clearInterval(timerTick); timerTick = null; }
  }

  /* ── Timer helpers ── */

  function formatDuration(ms) {
    if (ms <= 0) return '0:00';
    var totalSecs = Math.floor(ms / 1000);
    var mins = Math.floor(totalSecs / 60);
    var secs = totalSecs % 60;
    return mins + ':' + (secs < 10 ? '0' : '') + secs;
  }

  function formatHashrate(h) {
    if (h == null) return '—';
    if (h >= 1000) return (h / 1000).toFixed(1) + ' kH/s';
    return parseFloat(h).toFixed(1) + ' H/s';
  }

  function tickTimer() {
    var rig    = appState.rig || {};
    var status = rig.status || 'idle';

    var phaseEl  = document.getElementById('rigTimerPhase');
    var valueEl  = document.getElementById('rigTimerValue');
    var labelEl  = document.getElementById('rigTimerLabel');
    var telRow   = document.getElementById('rigTelemetryRow');

    if (status === 'active') {
      var remaining = appState.sessionEndTime ? (appState.sessionEndTime - Date.now()) : -1;
      if (phaseEl) { phaseEl.textContent = 'Session Running'; phaseEl.className = 'rig-timer-phase active'; }
      if (valueEl) valueEl.textContent = remaining > 0 ? formatDuration(remaining) : '—';
      if (labelEl) {
        var durMin = rig.activeSessionDurationMinutes;
        labelEl.textContent = durMin ? 'remaining in ' + durMin + 'm session' : 'remaining in session';
      }
      if (telRow) telRow.style.display = '';
      // Update heartbeat age on every tick
      if (rig.liveTelemetryAt) {
        var hbAge = Math.floor((Date.now() - new Date(rig.liveTelemetryAt).getTime()) / 1000);
        setText('rigHeartbeat', hbAge + 's ago');
      }
    } else if (status === 'cooldown') {
      var remaining = appState.cooldownEndTime ? (appState.cooldownEndTime - Date.now()) : -1;
      if (phaseEl) { phaseEl.textContent = 'Cooldown'; phaseEl.className = 'rig-timer-phase cooldown'; }
      if (valueEl) valueEl.textContent = remaining > 0 ? formatDuration(remaining) : 'Ending...';
      if (labelEl) labelEl.textContent = 'until next session ready';
      if (telRow) telRow.style.display = 'none';
    } else if (status === 'authorized') {
      if (phaseEl) { phaseEl.textContent = 'Authorized'; phaseEl.className = 'rig-timer-phase authorized'; }
      if (valueEl) valueEl.textContent = 'Starting...';
      if (labelEl) labelEl.textContent = 'controller is launching session';
      if (telRow) telRow.style.display = 'none';
    } else {
      if (phaseEl) { phaseEl.textContent = 'Idle'; phaseEl.className = 'rig-timer-phase idle'; }
      if (valueEl) valueEl.textContent = '—';
      if (labelEl) labelEl.textContent = 'waiting for session trigger';
      if (telRow) telRow.style.display = 'none';
    }
  }

  function renderUsage() {
    if (!appState.usage) return;
    const t = appState.usage.tokenUsage;
    const budgetText = t.remaining + ' / ' + t.limit;
    const swapBudget = document.getElementById('swapBudgetRemaining');
    if (swapBudget) swapBudget.textContent = budgetText;
    const burnBudget = document.getElementById('burnBudgetRemaining');
    if (burnBudget) burnBudget.textContent = budgetText;
    setText('personalTokensUsed', t.totalUsed + ' / ' + t.limit);
    setText('personalSwapsAttempted', String(t.countSwapsUsed || 0));
    setText('personalSwapsCounted', String(t.burnTokensUsed || 0));
    var hbUsed = (appState.usage.hashboostUsage && appState.usage.hashboostUsage.totalUsed) || 0;
    setText('personalHashboostUsed', String(hbUsed));
  }

  function renderHashboostBucket() {
    var rigBucket  = (appState.rig && appState.rig.hashboostBucket) || null;
    var userBucket = appState.hashboostBucket || null;
    var auth       = appState.authenticated;

    var msgEl    = document.getElementById('hashboostStatusMessage');
    var barEl    = document.getElementById('hashboostProgressBar');
    var progEl   = document.getElementById('hashboostProgressText');
    var submitBtn = document.getElementById('btnSubmitHashboost');

    var committed     = rigBucket ? (rigBucket.dropletsCommitted || 0) : 0;
    var status        = rigBucket ? (rigBucket.status || 'none') : 'none';
    var dailyTriggers = rigBucket ? (rigBucket.dailyTriggers || 0) : 0;

    var userBucketUsed = (userBucket && auth) ? (userBucket.userDroplets || 0) : 0;
    var userDayUsed    = (auth && appState.usage) ? (appState.usage.hashboostUsage.totalUsed || 0) : 0;

    setText('hashboostUserBucket',    userBucketUsed + ' / 2');
    setText('hashboostUserDay',       userDayUsed    + ' / 3');
    setText('hashboostDailyTriggers', dailyTriggers  + ' / 10');

    var pct = Math.min(100, Math.round((committed / 6) * 100));
    if (barEl)  barEl.style.width = pct + '%';
    if (progEl) progEl.textContent = committed + ' / 6 Droplets';

    var msg        = '';
    var btnDisable = true;

    if (!rigBucket || status === 'none') {
      msg = 'No active bucket. A new one opens after the current session ends.';
    } else if (status === 'blocked_daily_limit' || dailyTriggers >= 10) {
      msg = 'Maximum Hashboost triggers reached for today. Contributions reopen tomorrow.';
    } else if (status === 'filled') {
      msg = 'Hashboost queued for the next session. Contributions reopen after that session ends.';
    } else if (status === 'expired') {
      msg = 'This attempt expired at session start. Contributed points were kept.';
    } else if (status === 'open') {
      msg = 'Contribute up to 2 Droplets toward the next Hashboost attempt.';
      btnDisable = !auth;
    }

    if (msgEl)    msgEl.textContent  = msg;
    if (submitBtn) {
      submitBtn.disabled    = btnDisable;
      submitBtn.style.opacity = btnDisable ? '0.45' : '';
      submitBtn.style.cursor  = btnDisable ? 'not-allowed' : '';
    }
  }

  /* ── Community meta lookup (driven by CommunityRegistry) ── */
  function getCommunityMeta(id) {
    var key = (id || 'unknown').toLowerCase();
    if (window.CommunityRegistry) {
      var all = window.CommunityRegistry.getAll();
      for (var i = 0; i < all.length; i++) {
        if (all[i].id === key) return { name: all[i].name, logo: all[i].logo };
      }
    }
    // Graceful fallback — capitalise the id so it at least looks reasonable
    return { name: id ? id.charAt(0).toUpperCase() + id.slice(1) : 'Unknown', logo: '' };
  }

  function renderRewards() {
    if (!appState.rewards) return;
    const totals  = appState.rewards.totals;
    const summary = appState.rewards.allTimeSummary;

    /* ── Top-level totals ── */
    const swapBucket   = totals.swapBucket      || {};
    const boostBucket  = totals.hashboostBucket || {};
    const swapWeight   = swapBucket.totalRewardWeight  || 0;
    const boostWeight  = boostBucket.totalRewardWeight || 0;
    const boostDroplets = boostBucket.totalAmount || 0;

    setText('rewardTotalTokens',  String((swapBucket.totalAmount || 0) + boostDroplets));
    setText('rewardTotalWeight',  String(swapWeight + boostWeight));
    setText('rewardTotalEntries', String(totals.totalEntryCount || 0));

    /* ── Participation weight breakdown ── */
    const countSwapsAmt  = swapBucket.countSwapsAmount  || 0;
    const burnTokensAmt  = swapBucket.burnTokensAmount  || 0;
    const burnWeight     = Math.round((burnTokensAmt * 0.5) * 1000) / 1000;

    setText('rewardSwapTokens',     String(countSwapsAmt));
    setText('rewardSwapWeight',     String(countSwapsAmt));
    setText('rewardBurnTokens',     String(burnTokensAmt));
    setText('rewardBurnWeight',     String(burnWeight));
    setText('rewardHashboostAmount', boostDroplets ? String(boostDroplets) + ' Droplet' + (boostDroplets !== 1 ? 's' : '') : '—');
    setText('rewardHashboostWeight', String(boostBucket.totalRewardWeight || 0));

    /* ── Community breakdown ── */
    const container = document.getElementById('rewardCommunityBreakdown');
    if (!container) return;

    const communities = ((summary && summary.swapBucket && summary.swapBucket.communityBreakdown) || [])
      .slice()
      .sort(function (a, b) {
        var wA = (a.countSwapsAmount || 0) + Math.round((a.burnTokensAmount || 0) * 0.5 * 1000) / 1000;
        var wB = (b.countSwapsAmount || 0) + Math.round((b.burnTokensAmount || 0) * 0.5 * 1000) / 1000;
        return wB - wA;
      });
    if (!communities.length) {
      container.innerHTML = '<p style="color:var(--text-secondary);font-size:0.85rem;padding:8px 0;">No community contributions yet.</p>';
      return;
    }

    container.innerHTML = communities.map(function (c) {
      var key      = (c.community || 'unknown').toLowerCase();
      var meta     = getCommunityMeta(key);
      var logo     = meta.logo;
      var name     = meta.name;
      const swapTkns = c.countSwapsAmount    || 0;
      const swapWt   = swapTkns;
      const burnTkns = c.burnTokensAmount    || 0;
      const burnWt   = Math.round((burnTkns * 0.5) * 1000) / 1000;
      return '<div class="community-breakdown-card">' +
        '<div class="community-breakdown-header">' +
          (logo ? '<img src="' + logo + '" alt="" class="community-breakdown-logo" />' : '') +
          '<span class="community-breakdown-name">' + name + '</span>' +
        '</div>' +
        '<div class="community-breakdown-stats">' +
          '<div class="community-breakdown-stat"><span class="community-breakdown-stat-label">Swap Tkns</span><span class="community-breakdown-stat-value">' + swapTkns + '</span></div>' +
          '<div class="community-breakdown-stat"><span class="community-breakdown-stat-label">Swap Wt</span><span class="community-breakdown-stat-value">'   + swapWt   + '</span></div>' +
          '<div class="community-breakdown-stat"><span class="community-breakdown-stat-label">Burn Tkns</span><span class="community-breakdown-stat-value">' + burnTkns + '</span></div>' +
          '<div class="community-breakdown-stat"><span class="community-breakdown-stat-label">Burn Wt</span><span class="community-breakdown-stat-value">'   + burnWt   + '</span></div>' +
        '</div>' +
      '</div>';
    }).join('');
  }

  /* ── Auth ── */

  function renderAuthState() {
    var out = document.getElementById('authLoggedOut');
    var inn = document.getElementById('authLoggedIn');
    if (appState.authenticated) {
      if (out) out.style.display = 'none';
      if (inn) inn.style.display = 'block';
      var short = appState.wallet
        ? appState.wallet.slice(0, 4) + '...' + appState.wallet.slice(-4)
        : '—';
      setText('authWalletAddress', short);
      if (appState.expiresAt) {
        var mins = Math.round((new Date(appState.expiresAt) - Date.now()) / 60000);
        setText('authSessionExpiry', 'Expires in ' + Math.max(0, mins) + ' min');
      }
    } else {
      if (out) out.style.display = 'block';
      if (inn) inn.style.display = 'none';
    }
    bindOnce('btnVerifyWallet', handleVerifyWallet);
    bindOnce('btnLogout', handleLogout);
    // Re-render leaderboard so the YOU badge reflects current wallet state
    if (appState.leaderboard) renderLeaderboard(appState.leaderboard);
  }

  /* ── Wallet sign helper ── */

  function encodeBase58(bytes) {
    var ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
    var digits = [0];
    for (var i = 0; i < bytes.length; i++) {
      var carry = bytes[i];
      for (var j = 0; j < digits.length; j++) {
        carry += digits[j] << 8;
        digits[j] = carry % 58;
        carry = (carry / 58) | 0;
      }
      while (carry) { digits.push(carry % 58); carry = (carry / 58) | 0; }
    }
    var result = '';
    for (var k = 0; bytes[k] === 0 && k < bytes.length - 1; k++) result += '1';
    for (var k = digits.length - 1; k >= 0; k--) result += ALPHABET[digits[k]];
    return result;
  }

  async function handleVerifyWallet() {
    if (!appState.accepted) { showAcceptanceModal(); return; }

    var walletState = window.AquaVaultsHeader
      ? window.AquaVaultsHeader.getWalletState()
      : { connected: false };
    if (!walletState.connected || !walletState.address) {
      alert('Please connect your wallet first.');
      return;
    }
    var wallet = walletState.address;

    var btn = document.getElementById('btnVerifyWallet');
    if (btn) { btn.disabled = true; btn.textContent = 'Verifying...'; }

    try {
      var challenge = await fetchChallenge(wallet);
      var messageBytes = new TextEncoder().encode(challenge.message);

      var signResult;
      if (window.AquaVaultsWallet && typeof window.AquaVaultsWallet.signMessage === 'function') {
        signResult = await window.AquaVaultsWallet.signMessage(messageBytes);
      } else if (window.solana && typeof window.solana.signMessage === 'function') {
        signResult = await window.solana.signMessage(messageBytes, 'utf8');
      } else {
        throw new Error('No wallet available to sign message');
      }

      var sigBytes = signResult.signature || signResult;
      var signature = encodeBase58(sigBytes instanceof Uint8Array ? sigBytes : new Uint8Array(sigBytes));

      await fetchVerify(wallet, challenge.nonce, signature);

      var who = await fetchWhoami();
      if (who.authenticated) {
        appState.authenticated = true;
        appState.wallet = who.wallet;
        appState.expiresAt = who.expiresAt;
      }
      renderAuthState();
      await refreshAuthenticatedData();
    } catch (e) {
      console.error('[Co-Op Rig] Verify failed:', e);
      alert('Verification failed: ' + (e.error || e.message || 'Unknown error'));
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = 'Verify Wallet'; }
    }
  }

  async function handleLogout() {
    try {
      await fetchLogout();
    } catch (e) {
      console.warn('[Co-Op Rig] logout error:', e);
    }
    appState.authenticated = false;
    appState.wallet = null;
    appState.expiresAt = null;
    appState.usage = null;
    appState.rewards = null;
    stopRigPolling();
    renderAuthState();
    renderRigStatus();
  }

  /* ── Rig Status ── */

  function renderRigStatus() {
    var dot = document.getElementById('rigStatusDot');
    var rig = appState.rig || {};
    var status = rig.status || 'idle';

    if (dot) dot.className = 'rig-status-dot ' + status;
    setText('rigStatusLabel', status.charAt(0).toUpperCase() + status.slice(1));

    var modeNotice = document.getElementById('rigModeNotice');
    if (modeNotice) {
      if (rig.modeNotice) {
        modeNotice.textContent = '⚠ ' + rig.modeNotice;
        modeNotice.style.display = 'block';
      } else {
        modeNotice.style.display = 'none';
      }
    }

    var counted = (rig.countedSwaps != null) ? rig.countedSwaps : '—';
    var target  = (rig.countedSwapsTarget != null) ? rig.countedSwapsTarget : '—';
    setText('rigCountedSwaps', counted + ' / ' + target);

    var boosts    = (rig.boostsEarned != null) ? rig.boostsEarned : '—';
    var boostReq  = (rig.boostsRequired != null) ? rig.boostsRequired : '—';
    setText('rigBoosts', boosts + ' / ' + boostReq);

    var trigger = document.getElementById('rigSessionTrigger');
    if (trigger) {
      var ready = !!rig.sessionReady;
      trigger.textContent = ready ? 'Ready' : 'Not Ready';
      var p = trigger.closest('.session-progress-item');
      if (p) { p.classList.remove('ready', 'not-ready'); p.classList.add(ready ? 'ready' : 'not-ready'); }
    }

    setText('rigManifestSwaps', (rig.manifestSwaps != null) ? String(rig.manifestSwaps) : '—');
    setText('rigManifestBx',    (rig.manifestBxSwaps != null) ? String(rig.manifestBxSwaps) : '—');
    setText('rigSessionId',     rig.activeSessionId    || '—');
    setText('rigCooldown',      rig.cooldownRemaining  || '—');
    setText('rigCurrentDuration', rig.currentDuration  || '—');
    setText('rigNextDuration',  rig.nextDuration        || '—');
    setText('rigPendingBurnMin', rig.pendingBurnMin     || '—');
    setText('rigPendingHashboost', rig.pendingHashboost && Number(rig.pendingHashboost) > 0 ? rig.pendingHashboost + ' SOL' : '0 SOL');

    var totalSessions = (rig.totalSessionCount != null) ? String(rig.totalSessionCount) : '—';
    setText('rigTotalSessions', totalSessions);

    var queued = (rig.queuedSessions != null) ? String(rig.queuedSessions) : '0';
    setText('rigQueuedSessions', queued);

    var bucketStatus = rig.hashboostBucket ? rig.hashboostBucket.status : 'none';
    var bucketText = '0 / 6';
    if (bucketStatus === 'filled') {
      bucketText = '⚡ Full';
    } else if (bucketStatus === 'open') {
      var committed = rig.hashboostBucket.dropletsCommitted || 0;
      var target    = rig.hashboostBucket.dropletsTarget    || 6;
      bucketText = committed + ' / ' + target;
    } else if (bucketStatus === 'blocked_daily_limit') {
      bucketText = 'Daily limit hit';
    }
    var bucketEl = document.getElementById('rigHashboostBucket');
    if (bucketEl) {
      bucketEl.textContent = bucketText;
      bucketEl.style.color = bucketStatus === 'filled' ? 'var(--coop-accent)' : '';
      bucketEl.style.fontWeight = bucketStatus === 'filled' ? '600' : '';
    }

    // ── Timer anchors (computed once per poll; tickTimer() interpolates between polls) ──
    if (rig.sessionStartedAt && rig.activeSessionDurationMinutes != null) {
      appState.sessionEndTime = new Date(rig.sessionStartedAt).getTime() + (rig.activeSessionDurationMinutes * 60000);
    } else {
      appState.sessionEndTime = null;
    }
    appState.cooldownEndTime = rig.cooldownUntil ? new Date(rig.cooldownUntil).getTime() : null;

    // ── Live telemetry (static values updated each poll; heartbeat age ticks per second) ──
    setText('rigHashrate',  formatHashrate(rig.liveHashrate));
    setText('rigUnclaimed', rig.liveUnclaimed || '—');

    // Immediate tick so the display reflects new data without waiting for the interval
    tickTimer();
  }

  /* ── How It Works accordion ── */

  function bindHowItWorks() {
    var trigger = document.getElementById('howItWorksTrigger');
    var body    = document.getElementById('howItWorksBody');
    if (!trigger || !body) return;
    trigger.addEventListener('click', function () {
      var isOpen = body.classList.toggle('open');
      trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  }

  /* ── Tab switching ── */

  function bindTabs() {
    var tabs = document.querySelectorAll('.coop-tab');
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        tabs.forEach(function (t) { t.classList.remove('active'); });
        tab.classList.add('active');
        document.querySelectorAll('.coop-tab-panel').forEach(function (p) { p.classList.remove('active'); });
        var panel = document.getElementById('panel-' + tab.getAttribute('data-tab'));
        if (panel) panel.classList.add('active');
      });
    });
  }

  /* ── Action type toggle ── */

  function bindActionTypeToggle() {
    var btns = document.querySelectorAll('.action-type-btn');
    btns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        btns.forEach(function (b) { b.classList.remove('active'); });
        btn.classList.add('active');
        document.querySelectorAll('.action-form').forEach(function (f) { f.classList.remove('active'); });
        var form = document.getElementById('form-' + btn.getAttribute('data-action'));
        if (form) form.classList.add('active');
        clearSubmitResult('swapResult');
        clearSubmitResult('burnResult');
        clearSubmitResult('hashboostResult');
      });
    });
  }

  /* ── Community selector ── */

  function bindCommunitySelector() {
    var trigger  = document.getElementById('contributeCommunity');
    var dropdown = document.getElementById('contributeCommunityDropdown');
    if (!trigger || !dropdown) return;

    // Toggle open/close on trigger click
    trigger.addEventListener('click', function (e) {
      if (dropdown.contains(e.target)) return;
      var isOpen = trigger.classList.toggle('open');
      dropdown.classList.toggle('open', isOpen);
    });

    // Close on outside click
    document.addEventListener('click', function (e) {
      if (!trigger.contains(e.target)) {
        trigger.classList.remove('open');
        dropdown.classList.remove('open');
      }
    });

    // Populate from registry (retries until available)
    populateCommunityDropdown(trigger, dropdown, 0);
  }

  function populateCommunityDropdown(trigger, dropdown, attempt) {
    var MAX_ATTEMPTS = 20;
    if (!window.CommunityRegistry) {
      if (attempt >= MAX_ATTEMPTS) {
        console.warn('[Co-Op Rig] CommunityRegistry not available after retries — dropdown will be empty');
        return;
      }
      setTimeout(function () { populateCommunityDropdown(trigger, dropdown, attempt + 1); }, 100);
      return;
    }

    var communities = window.CommunityRegistry.getAll();
    dropdown.innerHTML = '';

    communities.forEach(function (community) {
      var item = document.createElement('div');
      item.className = 'contribute-community-option';
      item.setAttribute('data-community', community.id);
      item.innerHTML =
        '<img src="' + community.logo + '" alt="" class="contribute-community-option-logo" />' +
        '<span class="contribute-community-option-name">' + community.name + '</span>';
      item.addEventListener('click', function (e) {
        e.stopPropagation();
        applyCommunitySelection(community, trigger, dropdown);
      });
      dropdown.appendChild(item);
    });

    // Restore persisted selection, fall back to first community
    var savedId     = localStorage.getItem('selectedCommunity');
    var defaultComm = communities[0];
    if (savedId) {
      var found = communities.find(function (c) { return c.id === savedId; });
      if (found) defaultComm = found;
    }
    if (defaultComm) applyCommunitySelection(defaultComm, trigger, dropdown);
  }

  function applyCommunitySelection(community, trigger, dropdown) {
    appState.selectedCommunity = community.id;
    localStorage.setItem('selectedCommunity', community.id);
    setText('contributeCommunityName', community.name);
    var logoEl = document.getElementById('contributeCommunityLogo');
    if (logoEl) logoEl.src = community.logo;
    if (trigger)  trigger.classList.remove('open');
    if (dropdown) dropdown.classList.remove('open');
  }

  /* ── Acceptance modal ── */

  function bindAcceptanceModal() {
    bindOnce('btnAcceptanceCancel', hideAcceptanceModal);
    bindOnce('btnAcceptanceContinue', function () {
      appState.accepted = true;
      localStorage.setItem('coop_accepted', 'true');
      hideAcceptanceModal();
    });
  }

  function showAcceptanceModal() {
    var el = document.getElementById('coopAcceptanceOverlay');
    if (el) el.style.display = 'flex';
  }

  function hideAcceptanceModal() {
    var el = document.getElementById('coopAcceptanceOverlay');
    if (el) el.style.display = 'none';
  }

  /* ── Action buttons ── */

  /* ── Submission feedback ── */

  function showSubmitResult(id, html, type) {
    var el = document.getElementById(id);
    if (!el) return;
    el.className = 'submit-result show ' + (type || 'success');
    el.innerHTML = html;
  }

  function clearSubmitResult(id) {
    var el = document.getElementById(id);
    if (!el) return;
    el.className = 'submit-result';
    el.innerHTML = '';
  }

  function buildSwapFeedback(droplets, community) {
    var rig     = appState.rig || {};
    var status  = rig.status  || 'idle';
    var counted = rig.countedSwaps       != null ? rig.countedSwaps       : '—';
    var target  = rig.countedSwapsTarget != null ? rig.countedSwapsTarget : '—';
    var commName = getCommunityDisplayName(community);

    var header = '<span class="sr-check">✓</span> ' +
      droplets + ' Droplet' + (droplets !== 1 ? 's' : '') +
      ' contributed for <strong>' + escapeHtml(commName) + '</strong>';

    var context = '';
    if (status === 'active') {
      context = 'The rig is running a session now. Your Droplets are queued toward the next one.';
    } else if (status === 'cooldown') {
      context = 'Rig is in cooldown. Your Droplets are banked toward the next session.';
    } else if (status === 'authorized') {
      context = 'Session is authorized and starting. Your Droplets are banked for the one after.';
    } else {
      // idle — show progress
      if (counted !== '—' && target !== '—') {
        var remaining = Math.max(0, target - counted);
        if (remaining === 0) {
          context = '🎯 Session trigger reached — the rig is ready to start the next session.';
        } else {
          context = counted + ' / ' + target + ' swaps banked — ' + remaining + ' more to trigger a session.';
        }
      } else {
        context = 'Droplets banked toward the next session trigger.';
      }
    }

    return '<div class="sr-header">' + header + '</div>' +
           '<div class="sr-context">' + context + '</div>';
  }

  function buildBurnFeedback(burnTokens, community) {
    var rig      = appState.rig || {};
    var nextDur  = rig.nextDuration  || '—';
    var pendMin  = rig.pendingBurnMin != null ? rig.pendingBurnMin : '—';
    var commName = getCommunityDisplayName(community);
    var addedMin = burnTokens * 2;

    var header = '<span class="sr-check">✓</span> ' +
      burnTokens + ' Droplet' + (burnTokens !== 1 ? 's' : '') +
      ' burned for <strong>' + escapeHtml(commName) + '</strong>';

    var context = '+' + addedMin + ' minutes added.';
    if (pendMin !== '—' && pendMin > 0) {
      context += ' Next session duration: <strong>' + nextDur + '</strong> (' + pendMin + ' pending burn min total).';
    } else {
      context += ' Next session: <strong>' + nextDur + '</strong>.';
    }

    return '<div class="sr-header">' + header + '</div>' +
           '<div class="sr-context">' + context + '</div>';
  }

  function buildHashboostFeedback(droplets, community) {
    var rig        = appState.rig || {};
    var bucket     = rig.hashboostBucket || {};
    var status     = bucket.status || 'none';
    var committed  = bucket.dropletsCommitted || 0;
    var bucketTarget = bucket.dropletsTarget  || 6;
    var commName   = getCommunityDisplayName(community);
    var pts        = (droplets * 1.5).toFixed(1);

    var header = '<span class="sr-check">✓</span> ' +
      droplets + ' Droplet' + (droplets !== 1 ? 's' : '') +
      ' contributed for <strong>' + escapeHtml(commName) + '</strong> (+' + pts + ' pts)';

    var context = '';
    if (status === 'filled') {
      context = '⚡ Bucket filled! 0.01 SOL is queued for the next session boost.';
    } else if (status === 'open') {
      var remaining = Math.max(0, bucketTarget - committed);
      context = committed + ' / ' + bucketTarget + ' Droplets in bucket — ' +
        remaining + ' more needed to fill.';
    } else {
      context = 'Droplets accepted. Points recorded.';
    }

    return '<div class="sr-header">' + header + '</div>' +
           '<div class="sr-context">' + context + '</div>';
  }

  function buildErrorFeedback(msg) {
    return '<span class="sr-check sr-x">✗</span> ' + escapeHtml(msg);
  }

  function bindActionButtons() {
    // Burn token preview
    var burnInput = document.getElementById('burnTokenInput');
    if (burnInput) {
      burnInput.addEventListener('input', function () {
        var v = parseInt(burnInput.value, 10) || 0;
        setText('burnPreviewMinutes', '+' + (v * 2) + ' min');
      });
    }

    // MAX buttons
    var swapMaxBtn = document.getElementById('swapMaxBtn');
    if (swapMaxBtn) {
      swapMaxBtn.addEventListener('click', function () {
        var rem = appState.usage ? appState.usage.tokenUsage.remaining : 6;
        var inp = document.getElementById('swapTokenInput');
        if (inp) inp.value = Math.max(0, rem);
      });
    }
    var burnMaxBtn = document.getElementById('burnMaxBtn');
    if (burnMaxBtn) {
      burnMaxBtn.addEventListener('click', function () {
        var rem = appState.usage ? appState.usage.tokenUsage.remaining : 6;
        var inp = document.getElementById('burnTokenInput');
        if (inp) { inp.value = Math.max(0, rem); inp.dispatchEvent(new Event('input')); }
      });
    }

    bindOnce('btnSubmitSwap', async function () {
      if (!appState.authenticated) { alert('Please verify your wallet first.'); return; }
      var tokensUsed = parseInt(document.getElementById('swapTokenInput').value, 10);
      if (!tokensUsed || tokensUsed < 1) { alert('Enter a valid token amount (minimum 1).'); return; }
      var btn = document.getElementById('btnSubmitSwap');
      if (btn) { btn.disabled = true; btn.textContent = 'Submitting...'; }
      clearSubmitResult('swapResult');
      var requestId = crypto.randomUUID();
      var community = appState.selectedCommunity;
      try {
        var rigWallet = (appState.rig && (appState.rig.rigWallet || appState.rig.wallet)) || '';
        await submitCountSwaps(community, tokensUsed, tokensUsed, rigWallet, requestId);
        document.getElementById('swapTokenInput').value = '';
        await refreshAuthenticatedData();
        showSubmitResult('swapResult', buildSwapFeedback(tokensUsed, community), 'success');
      } catch (e) {
        console.error('[Co-Op Rig] Swap submit failed:', e);
        showSubmitResult('swapResult', buildErrorFeedback(e.error || e.message || 'Submission failed'), 'error');
      } finally {
        if (btn) { btn.disabled = false; btn.textContent = 'Contribute Swaps'; }
      }
    });

    bindOnce('btnSubmitBurn', async function () {
      if (!appState.authenticated) { alert('Please verify your wallet first.'); return; }
      var burnTokens = parseInt(document.getElementById('burnTokenInput').value, 10);
      if (!burnTokens || burnTokens < 1) { alert('Enter a valid burn amount (minimum 1).'); return; }
      var btn = document.getElementById('btnSubmitBurn');
      if (btn) { btn.disabled = true; btn.textContent = 'Submitting...'; }
      clearSubmitResult('burnResult');
      var requestId = crypto.randomUUID();
      var community = appState.selectedCommunity;
      try {
        await submitBurnTokens(community, burnTokens, requestId);
        document.getElementById('burnTokenInput').value = '';
        setText('burnPreviewMinutes', '+0 min');
        await refreshAuthenticatedData();
        showSubmitResult('burnResult', buildBurnFeedback(burnTokens, community), 'success');
      } catch (e) {
        console.error('[Co-Op Rig] Burn submit failed:', e);
        showSubmitResult('burnResult', buildErrorFeedback(e.error || e.message || 'Submission failed'), 'error');
      } finally {
        if (btn) { btn.disabled = false; btn.textContent = 'Burn Droplets'; }
      }
    });

    document.querySelectorAll('.hashboost-droplet-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        document.querySelectorAll('.hashboost-droplet-btn').forEach(function (b) { b.classList.remove('active'); });
        btn.classList.add('active');
        appState.selectedDroplets = parseInt(btn.getAttribute('data-droplets'), 10);
      });
    });
    
    bindOnce('btnSubmitHashboost', async function () {
      if (!appState.authenticated) { alert('Please verify your wallet first.'); return; }
      var droplets = appState.selectedDroplets || 1;
      var btn = document.getElementById('btnSubmitHashboost');
      if (btn) { btn.disabled = true; btn.textContent = 'Submitting...'; }
      clearSubmitResult('hashboostResult');
      var community = appState.selectedCommunity;
      try {
        await api('POST', '/api/coop/hashboost', { community, droplets });
        await refreshAuthenticatedData();
        showSubmitResult('hashboostResult', buildHashboostFeedback(droplets, community), 'success');
      } catch (e) {
        console.error('[Co-Op Rig] Hashboost submit failed:', e);
        showSubmitResult('hashboostResult', buildErrorFeedback(e.error || e.message || 'Submission failed'), 'error');
      } finally {
        if (btn) { btn.disabled = false; btn.textContent = 'Submit Hashboost'; }
      }
    });
  }

  /* ── Session History ── */

  function renderSessionHistory(sessions) {
    var container = document.getElementById('sessionHistoryList');
    if (!container) return;

    if (!sessions || !sessions.length) {
      container.innerHTML = '<p class="sh-empty">No sessions recorded yet.</p>';
      return;
    }

    function timeAgo(isoStr) {
      if (!isoStr) return '—';
      var diff = Math.floor((Date.now() - new Date(isoStr).getTime()) / 1000);
      if (diff < 60)    return diff + 's ago';
      if (diff < 3600)  return Math.floor(diff / 60) + 'm ago';
      if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
      return Math.floor(diff / 86400) + 'd ago';
    }

    function statusLabel(s) {
      switch (s) {
        case 'claimed':    return { text: 'Claimed',    cls: 'claimed' };
        case 'active':     return { text: 'Active',     cls: 'active' };
        case 'authorized': return { text: 'Authorized', cls: 'authorized' };
        case 'missed':     return { text: 'Missed',     cls: 'missed' };
        default:           return { text: s,            cls: 'idle' };
      }
    }

    container.innerHTML = sessions.map(function (s) {
      var st       = statusLabel(s.status);
      var when     = s.claimedAt || s.startedAt || s.createdAt;
      var duration = s.durationMinutes ? s.durationMinutes + 'm' : '—';
      var burnLine = s.burnTokens > 0 ? '+' + s.burnMinutes + 'm burn' : null;
      var boostLine = null;
      if (s.hashboostAmount > 0) {
        boostLine = s.hashboostSuccess ? '⚡ Boosted' : '⚡ Boost failed';
      }

      return '<div class="sh-row">' +
        '<div class="sh-row-left">' +
          '<span class="sh-seq">#' + s.sequence + '</span>' +
          '<span class="sh-badge ' + st.cls + '">' + st.text + '</span>' +
        '</div>' +
        '<div class="sh-row-center">' +
          '<span class="sh-duration">' + duration + (burnLine ? ' <span class="sh-burn">' + burnLine + '</span>' : '') + '</span>' +
          (boostLine ? '<span class="sh-boost">' + boostLine + '</span>' : '') +
        '</div>' +
        '<div class="sh-row-right">' +
          '<span class="sh-time">' + timeAgo(when) + '</span>' +
        '</div>' +
      '</div>';
    }).join('');
  }

  /* ── Leaderboard ── */

  function bindLeaderboardTabs() {
    var tabs = document.querySelectorAll('.lb-tab');
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        tabs.forEach(function (t) { t.classList.remove('active'); });
        tab.classList.add('active');
        document.querySelectorAll('.lb-panel').forEach(function (p) { p.classList.remove('active'); });
        var panel = document.getElementById('lbPanel' + tab.getAttribute('data-lb'));
        if (panel) panel.classList.add('active');
      });
    });
  }

  function getCommunityDisplayName(id) {
    if (window.CommunityRegistry) {
      var all = window.CommunityRegistry.getAll();
      for (var i = 0; i < all.length; i++) {
        if (all[i].id === id) return all[i].name;
      }
    }
    return id
      ? id.split(/[-_]/).map(function (w) { return w.charAt(0).toUpperCase() + w.slice(1); }).join(' ')
      : 'Unknown';
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function renderLeaderboard(data) {
    if (!data) return;

    // Cycle bar
    if (data.cycle) {
      setText('lbCycleLabel', 'Cycle ' + data.cycle.cycleNumber);
      setText('lbCycleDays',  'Day ' + data.cycle.dayInCycle + ' of 30');
      var dr = data.cycle.daysRemaining;
      setText('lbCycleRemaining', dr + (dr === 1 ? ' day' : ' days') + ' remaining');
    }

    // Community panel
    var commPanel = document.getElementById('lbPanelCommunities');
    if (commPanel) {
      if (!data.communities || !data.communities.length) {
        commPanel.innerHTML = '<p class="lb-empty">No community contributions this cycle yet.</p>';
      } else {
        commPanel.innerHTML = data.communities.map(function (c) {
          var isTop = c.rank === 1;
          var name  = escapeHtml(getCommunityDisplayName(c.community));
          var rankLabel = isTop ? '🏆' : '#' + c.rank;
          return '<div class="lb-row' + (isTop ? ' lb-row-top' : '') + '">' +
            '<span class="lb-rank">' + rankLabel + '</span>' +
            '<span class="lb-name">' + name + '</span>' +
            '<div class="lb-stats">' +
              '<span class="lb-weight">' + c.totalWeight.toFixed(1) + ' pts</span>' +
              '<span class="lb-share">' + c.sharePct.toFixed(1) + '%</span>' +
              '<span class="lb-meta">' + c.contributorCount + ' users</span>' +
            '</div>' +
          '</div>';
        }).join('');
      }
    }

    // User panel
    var userPanel = document.getElementById('lbPanelUsers');
    if (userPanel) {
      if (!data.users || !data.users.length) {
        userPanel.innerHTML = '<p class="lb-empty">No user contributions this cycle yet.</p>';
      } else {
        var myWallet = appState.wallet;
        userPanel.innerHTML = data.users.map(function (u) {
          var isMe  = !!(myWallet && u.wallet === myWallet);
          var short = u.wallet.slice(0, 4) + '...' + u.wallet.slice(-4);
          var youBadge = isMe ? ' <span class="lb-you">YOU</span>' : '';
          return '<div class="lb-row' + (isMe ? ' lb-row-me' : '') + '">' +
            '<span class="lb-rank">#' + u.rank + '</span>' +
            '<span class="lb-name mono">' + short + youBadge + '</span>' +
            '<div class="lb-stats">' +
              '<span class="lb-weight">' + u.totalWeight.toFixed(1) + ' pts</span>' +
              '<span class="lb-share">' + u.sharePct.toFixed(1) + '%</span>' +
              '<span class="lb-meta">' + u.entryCount + ' entries</span>' +
            '</div>' +
          '</div>';
        }).join('');
      }
    }
  }

  /* ── Helpers ── */

  function setText(id, text) { var el = document.getElementById(id); if (el) el.textContent = text; }

  var _bound = {};
  function bindOnce(id, fn) {
    if (_bound[id]) return;
    var el = document.getElementById(id);
    if (el) { el.addEventListener('click', fn); _bound[id] = true; }
  }

})();